import { MethodFields } from '@/components/admin/reglages/method-fields';
import { VersionedEditor } from '@/components/admin/versioned/versioned-editor';
import { methodApi } from '@/lib/api';
import type { MethodDraft } from '@/lib/method-draft';
import { METHOD_DRAFT_KIT } from '@/lib/method-draft';
import { METHOD_SEED_AUTHOR_NAME, methodRefusalSentence, seedFootnote } from '@/lib/method-labels';
import { METHOD_WORDING } from '@/lib/method-wording';
import type {
  MethodContent,
  MethodDetail,
  MethodDraftContent,
  MethodVersion,
  VersionSummary,
} from '@matter_extractor/shared';

/**
 * The editor of « Méthode » (analyste.md §4, issue #196): the pattern of §4 in its
 * `settings` logement, plus the three functions and the six styles.
 *
 * Everything the other editors do identically - the head, the history, the save
 * box and « Passer en production », the debounced autosave and its optimistic
 * control, undo and redo - is `VersionedEditor`. What is here is what only
 * Méthode knows: its API, its draft kit, its wording and its fields, which became
 * the DRAFT: a word typed is an autosave, and « Enregistrer une nouvelle version »
 * is the one write.
 *
 * ## No « Passer en production » of its own, and no cascade
 *
 * The layer's gesture is the one used - « Passer en production » by the prod, the
 * cascade box of §4.3 - and Méthode takes nothing along: nothing cites it, and no
 * reference pins it (vitrine.md §4.2). The box lists it among the other
 * singletons.
 *
 * ## The cache keys
 *
 * `['method', email, METHOD_ID]` holds the detail and the layer writes every
 * answer into it. There is no list to invalidate - a singleton has none - so the
 * second prefix names one nobody reads, rather than the detail's own, which a
 * prefix invalidation would refetch after every autosave.
 */

/** The prefix of the detail's query key, shared with the route that reads it. */
export const METHOD_QUERY_KEY = 'method';

export interface MethodEditorProps {
  readonly detail: MethodDetail;
  /** A version opened read-only by « Voir », or null. */
  readonly viewing: MethodVersion | null;
  readonly onCloseView: () => void;
  readonly onView: (version: VersionSummary) => void;
  /** Re-read Méthode from the server - what the stale-draft box offers. */
  readonly onReload: () => void;
}

export function MethodEditor({
  detail,
  viewing,
  onCloseView,
  onView,
  onReload,
}: MethodEditorProps) {
  // The footnote of the planche, true only while the development version IS the
  // seed's: a v2 written by somebody is not « graine de #196 ».
  const seedNote =
    detail.development.createdBy === METHOD_SEED_AUTHOR_NAME
      ? seedFootnote(detail.development.number)
      : null;

  return (
    <VersionedEditor<MethodContent, MethodDraftContent, MethodDraft>
      wording={METHOD_WORDING}
      api={methodApi}
      kit={METHOD_DRAFT_KIT}
      keys={{ detail: METHOD_QUERY_KEY, list: 'method-sans-liste' }}
      detail={detail}
      viewing={viewing}
      onCloseView={onCloseView}
      onView={onView}
      onReload={onReload}
      onCreated={() => undefined}
      layout="settings"
      // A typed 400 of the save (a field the schema refuses) is worded from the
      // field it names; the layer falls back on its own sentence.
      ownRefusal={methodRefusalSentence}
      renderFields={({ draft, readOnly, onDraft }) => (
        <MethodFields
          draft={draft}
          stored={detail.developmentContent}
          readOnly={readOnly}
          seedNote={seedNote}
          onDraft={onDraft}
        />
      )}
    />
  );
}
